import axios from "axios";
import { useEffect, useState } from "react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true, timeout: 10000 });

export function formatApiError(e) {
  const detail = e?.response?.data?.detail;
  if (detail == null) return e?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/* ---- Offline resilience ----
   Every build ships a content snapshot (public/content-snapshot.json, generated at build time).
   If the API is unreachable, browsing still renders from it — only submissions need the API. */

let snapshotPromise = null;
function loadSnapshot() {
  // Retry on every call until a real snapshot object is loaded — never cache failures.
  if (snapshotPromise) return snapshotPromise;
  snapshotPromise = axios
    .get(`${window.location.origin}/content-snapshot.json`, { timeout: 6000 })
    .then((r) => {
      if (r.data && typeof r.data === "object" && Array.isArray(r.data.catalog)) return r.data;
      throw new Error("invalid snapshot"); // e.g. the SPA rewrite served index.html
    })
    .catch(() => {
      snapshotPromise = null; // allow a retry on the next request
      return null;
    });
  return snapshotPromise;
}

function matchSnapshot(snap, path) {
  if (!snap) return null;
  let m;
  if (path === "/catalog") return snap.catalog || [];
  if (path === "/services") return snap.services || [];
  if (path === "/collections") return snap.collections || [];
  if ((m = path.match(/^\/catalog\/([^/]+)$/))) return (snap.catalog || []).find((i) => i.slug === m[1]) || null;
  if ((m = path.match(/^\/services\/([^/]+)$/))) return (snap.services || []).find((i) => i.slug === m[1]) || null;
  if ((m = path.match(/^\/collections\/([^/]+)$/))) return (snap.collections || []).find((i) => i.slug === m[1]) || null;
  return null;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function useFetch(path) {
  const [state, setState] = useState({ data: null, loading: true, error: null, offline: false });
  useEffect(() => {
    let on = true;
    (async () => {
      setState((s) => ({ ...s, loading: true, error: null }));
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const r = await api.get(path);
          if (on) setState({ data: r.data, loading: false, error: null, offline: false });
          return;
        } catch (e) {
          const status = e?.response?.status;
          if (status && status < 500 && status !== 429) {
            // Authoritative server answer (e.g. 404 wrong slug) — no snapshot fallback.
            if (on) setState({ data: null, loading: false, error: formatApiError(e), offline: false });
            return;
          }
          if (attempt < 2) await sleep(600 * (attempt + 1));
        }
      }
      const matched = matchSnapshot(await loadSnapshot(), path);
      if (on && matched) {
        setState({ data: matched, loading: false, error: null, offline: true });
      } else if (on) {
        setState({
          data: null,
          loading: false,
          error: "The site could not reach the studio just now. Please try again in a moment.",
          offline: true,
        });
      }
    })();
    return () => {
      on = false;
    };
  }, [path]);
  return state;
}
